// Portal-aware anchor positioning.
//
// When a popover renders through `usePortalRoot()`, the portal target
// is either a registered chat surface (the `<EmbeddableChat>` wrapper
// or the standalone shell's `<div>`) or — when the provider isn't
// mounted — `document.body` as a last-resort fallback for isolated
// renders (Storybook fragments, vitest).
//
// Per-surface behaviour:
//
//   - **Registered chat surface** (embed or standalone). Carries
//     `position: relative`. The popover uses `position: absolute` and
//     coords expressed in the surface's local frame — viewport coords
//     minus the surface's top-left, with a small border-width
//     correction so a host that paints a CSS border on the surface
//     doesn't drift the popover anchor.
//   - **`document.body` fallback**. The popover uses `position: fixed`
//     and viewport-relative coords pass straight through.
//
// The embed surface additionally carries `overflow: hidden`, so an
// overflowing popover would be clipped; call sites clamp against the
// surface returned by `getPortalViewportSize` to keep the popover
// visible inside the embed instead.
//
// `resolveAnchorPosition` returns the coords + position mode the
// popover should apply. Call sites read the portal root via
// `usePortalRoot()` and pass it in alongside the desired viewport
// coords (already flip-adjusted and clamped).

import { useLayoutEffect, useRef, useState } from "react";

export interface AnchorPlacement {
  /** CSS top, in the portal's coordinate frame. */
  top: number;
  /** CSS left, in the portal's coordinate frame. */
  left: number;
  /** `"fixed"` for the `document.body` fallback, `"absolute"` when
   *  rendered inside a registered chat surface (embed or standalone). */
  position: "fixed" | "absolute";
}

interface AnchorContext {
  /** Portal target — the element returned by `usePortalRoot()`. */
  portalRoot: HTMLElement | null;
  /** Desired top in viewport coords (already flip-adjusted). */
  viewportTop: number;
  /** Desired left in viewport coords (already clamped). */
  viewportLeft: number;
}

/**
 * Translate viewport coords into the portal's local frame.
 *
 * - **`document.body` fallback** (no provider mounted): popover uses
 *   `position: fixed`; the viewport coords pass through unchanged.
 * - **Registered chat surface** (embed or standalone shell): popover
 *   uses `position: absolute`. Viewport coords are translated into
 *   the surface's local frame by subtracting its
 *   `getBoundingClientRect()` top/left.
 *
 * Edge cases:
 *
 *   - `getBoundingClientRect()` returns the *border-box* viewport
 *     coords. `position: absolute` resolves against the *padding-box*
 *     of its containing block. Subtracting `clientTop` / `clientLeft`
 *     (border widths) closes the gap so a host that paints a CSS
 *     border on the surface keeps the popover anchored to the trigger
 *     pixel-for-pixel.
 *   - `getBoundingClientRect()` accounts for ancestor scroll but does
 *     NOT include the element's own `scrollTop`/`scrollLeft`. Today
 *     the embed root carries `overflow: hidden`, so its `scrollTop`
 *     is structurally zero. We add `scrollTop`/`scrollLeft` anyway —
 *     it's a one-instruction defensive read that survives a future
 *     refactor making the surface scrollable (mobile-stacked layout,
 *     etc.) without silently breaking every popover anchor.
 */
export function resolveAnchorPosition({
  portalRoot,
  viewportTop,
  viewportLeft,
}: AnchorContext): AnchorPlacement {
  // No registered chat surface — either SSR (no `document`), no
  // provider, or the provider's fallback to `document.body`. Popover
  // pins to the viewport with `position: fixed`. Aligning the
  // discriminator with `isEmbeddedPortal` would be tighter (every
  // overlay component already uses that), but a registered standalone
  // shell goes through the absolute branch here too — same
  // mathematical result (the standalone shell is a full-viewport
  // `position: relative` div, so absolute against it ≡ fixed against
  // the viewport) — and the body-vs-non-body check makes the
  // fallback's intent explicit.
  if (typeof document === "undefined") {
    return { top: viewportTop, left: viewportLeft, position: "fixed" };
  }
  if (!portalRoot || portalRoot === document.body) {
    return { top: viewportTop, left: viewportLeft, position: "fixed" };
  }
  const rect = portalRoot.getBoundingClientRect();
  return {
    top: viewportTop - rect.top - portalRoot.clientTop + portalRoot.scrollTop,
    left: viewportLeft - rect.left - portalRoot.clientLeft + portalRoot.scrollLeft,
    position: "absolute",
  };
}

/**
 * Returns the dimensions of the surface the popover should clamp
 * itself within. Standalone: the viewport. Embed: the embed root's
 * inner box.
 *
 * The returned coords describe the *padding-box* surface — the
 * matching coordinate frame `resolveAnchorPosition` translates into.
 * Without this, a host that paints a CSS border on the embed root
 * would let the clamp run into the (clipped) border area.
 *
 * The embed root has `overflow: hidden`, so a popover that overflows
 * its box would simply be clipped. Clamping the position keeps the
 * popover visible inside the embed instead.
 */
export function getPortalViewportSize(portalRoot: HTMLElement | null): {
  width: number;
  height: number;
  offsetTop: number;
  offsetLeft: number;
} {
  if (typeof window === "undefined") {
    return { width: 0, height: 0, offsetTop: 0, offsetLeft: 0 };
  }
  if (!portalRoot || (typeof document !== "undefined" && portalRoot === document.body)) {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
      offsetTop: 0,
      offsetLeft: 0,
    };
  }
  const rect = portalRoot.getBoundingClientRect();
  // `clientWidth` / `clientHeight` are padding-box dimensions (excludes
  // border, includes padding). `rect.top + clientTop` is the
  // padding-box's top-left in viewport coords. Together they describe
  // the same surface `resolveAnchorPosition`'s padding-box translation
  // lands in, so the clamp and the transform agree.
  return {
    width: portalRoot.clientWidth,
    height: portalRoot.clientHeight,
    offsetTop: rect.top + portalRoot.clientTop,
    offsetLeft: rect.left + portalRoot.clientLeft,
  };
}

/**
 * Whether a portal target is the chat-embed surface specifically — the
 * `<EmbeddableChat>` wrapper marked with `data-nurion-chat-embed`.
 *
 * The standalone Tauri/web shell also registers its top-level `<div>`
 * with `EmbedPortalContext` (so dialogs / popovers stay inside the
 * `data-chat-root` font boundary), which means a plain
 * `portalRoot !== document.body` check would treat the standalone
 * shell as embedded. Keying off the embed-specific data attribute
 * keeps the embed-vs-standalone distinction honest — used by
 * `ImageLightbox` to decide whether locking `document.body`'s overflow
 * is allowed (it isn't inside an embed; it is in the standalone shell).
 */
export function isEmbeddedPortal(portalRoot: HTMLElement | null): boolean {
  if (typeof document === "undefined" || portalRoot === null || portalRoot === document.body) {
    return false;
  }
  return portalRoot.hasAttribute("data-nurion-chat-embed");
}

/** Surface the popover clamps within, as returned by `getPortalViewportSize`. */
export type PortalSurface = ReturnType<typeof getPortalViewportSize>;

/** Desired popover position in viewport coords, before portal-frame translation. */
export interface ViewportCoords {
  viewportTop: number;
  viewportLeft: number;
}

/**
 * Shared placement effect for portaled overlays. Tracks the anchor's
 * rect across capture-phase `scroll` + `resize`, computes the desired
 * viewport coords via the call site's `computeViewportCoords` (which
 * owns per-overlay flip/clamp math against `getPortalViewportSize`),
 * then translates them into the portal's frame with
 * `resolveAnchorPosition`. Returns `null` until the first measure, or
 * while `anchor` is `null`.
 *
 * `computeViewportCoords` is read through a ref so call sites can pass
 * an inline closure without re-subscribing the listeners on every
 * render — the effect re-runs only when `anchor` or `portalRoot`
 * changes, matching the per-overlay effects this collapses.
 */
export function usePortalPlacement({
  anchor,
  portalRoot,
  computeViewportCoords,
}: {
  anchor: HTMLElement | null;
  portalRoot: HTMLElement | null;
  computeViewportCoords: (anchorRect: DOMRect, surface: PortalSurface) => ViewportCoords;
}): AnchorPlacement | null {
  const [placement, setPlacement] = useState<AnchorPlacement | null>(null);
  const computeRef = useRef(computeViewportCoords);
  useLayoutEffect(() => {
    computeRef.current = computeViewportCoords;
  });
  useLayoutEffect(() => {
    if (!anchor) return;
    const update = () => {
      const rect = anchor.getBoundingClientRect();
      const surface = getPortalViewportSize(portalRoot);
      const { viewportTop, viewportLeft } = computeRef.current(rect, surface);
      setPlacement(resolveAnchorPosition({ portalRoot, viewportTop, viewportLeft }));
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [anchor, portalRoot]);
  return placement;
}
